import React, { CSSProperties } from 'react';
import Card from '../../components/layout/Card';
import HBox from '../../components/layout/HBox';
import Icon from '@mdi/react';
import { mdiClockAlert, mdiDrag, mdiClockRemove } from '@mdi/js';
import Subtitulo from '../../components/tipografia/Subtitulo';
import { Draggable } from 'react-beautiful-dnd';
import VBox from '../../components/layout/VBox';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const is24hExpired = (dataCriacao: string | null | undefined): boolean => {
  if (!dataCriacao) return false;
  return (Date.now() - new Date(dataCriacao).getTime()) / (1000 * 60 * 60) > 24;
};

const getItemStyle = (isDragging: boolean, draggableStyle: any, expirou24h: boolean): CSSProperties => ({
  userSelect: 'none',
  padding: 0,
  margin: 0,
  background: isDragging
    ? (expirou24h ? 'var(--tc-color-danger-200)' : 'var(--tc-color-warning-200)')
    : (expirou24h ? 'var(--tc-color-danger-50)' : 'var(--tc-color-warning-50)'),
  border: `1px solid ${expirou24h ? 'var(--tc-color-danger-400)' : 'var(--tc-color-warning-300)'}`,
  opacity: isDragging ? 0.5 : 1,
  minWidth: '200px',
  width: '200px',
  maxWidth: '250px',
  ...draggableStyle,
});

type CardEsperaProps = {
  item: {
    id: number;
    id_grupo: number;
    cliente_nome: string | null;
    data_criacao: string;
    ultimo_motivo: string | null;
    canal_cliente_id?: string | null;
    canal_id?: number | null;
  };
  index: number;
};

const CardEspera = ({ item, index }: CardEsperaProps) => {
  const navigate = useNavigate();
  const expirou24h = is24hExpired(item.data_criacao);
  const iconColor = expirou24h ? 'var(--tc-color-danger-600)' : 'var(--tc-color-warning-600)';
  const textColor = expirou24h ? 'var(--tc-color-danger-700)' : 'var(--tc-color-warning-700)';

  return (
    <Draggable
      key={`espera-${item.id}`}
      draggableId={`espera-${item.id}`}
      index={index}
    >
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          onClick={() => {
            if (!snapshot.isDragging && item.canal_cliente_id) {
              const params = new URLSearchParams({ ccid: item.canal_cliente_id });
              if (item.canal_id) params.set('canal_id', String(item.canal_id));
              navigate(`/chat?${params.toString()}`);
            }
          }}
          style={{
            gap: '2px',
            cursor: item.canal_cliente_id ? 'pointer' : undefined,
            ...getItemStyle(snapshot.isDragging, provided.draggableProps.style, expirou24h),
          }}
          title={
            expirou24h
              ? 'Mais de 24h em espera — será necessário enviar modelo de mensagem ao redirecionar'
              : (item.ultimo_motivo ? `Motivo: ${item.ultimo_motivo}` : 'Aguardando disponibilidade do departamento')
          }
        >
          <HBox style={{ alignContent: 'center', justifyContent: 'center', alignItems: 'center' }}>
            <span {...provided.dragHandleProps} style={{ display: 'flex', alignItems: 'center' }}>
              <Icon path={mdiDrag} size={1} color={iconColor} />
            </span>
            <VBox style={{ maxWidth: 'calc(100% - 30px)' }}>
              <HBox style={{ alignItems: 'center', justifyContent: 'center' }}>
                <Icon path={expirou24h ? mdiClockRemove : mdiClockAlert} size={1} color={iconColor} />
                <Subtitulo
                  style={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {item.cliente_nome ?? item.id_grupo?.toString() ?? '—'}
                </Subtitulo>
              </HBox>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  fontSize: '12px',
                  color: textColor,
                }}
              >
                <div>{expirou24h ? 'Janela expirada (>24h)' : 'Aguard. depto.'}</div>
                <div>
                  {item.data_criacao
                    ? 'Faz ' + formatDistanceToNow(new Date(item.data_criacao), { locale: ptBR })
                    : 'N/A'}
                </div>
              </div>
            </VBox>
          </HBox>
        </Card>
      )}
    </Draggable>
  );
};

export default CardEspera;
